import { House } from "lucide-react";
import { useMemo } from "react";

import { useCan } from "@/app/session/permissions";
import { useCurrentUser } from "@/app/session/useSession";
import { useActiveClients, useActiveStaff } from "@/features/projects/api";
import { NO_CLIENT, clientOption, personOption } from "@/features/projects/lib/pickerOptions";
import { useT } from "@/shared/i18n/useT";
import type { ComboboxOption } from "@/shared/ui/Combobox";

/**
 * The client picker's options: "No client (internal)" first, then the active clients, plus the
 * project's current client if it is no longer active. Unavailable to people who cannot list
 * clients (contractors), who then do not see the picker.
 */
export function useClientOptions(current: { id: string; name: string } | null = null) {
  const t = useT();
  const canView = useCan("clients.view");
  const clients = useActiveClients(canView);
  const options = useMemo<ComboboxOption[]>(() => {
    const list = clients.data ?? [];
    const missing = current && !list.some((client) => client.id === current.id) ? [current] : [];
    return [
      {
        value: NO_CLIENT,
        label: t("newProject.noClient"),
        leading: <House className="size-4 shrink-0 text-ink-faint" aria-hidden="true" />,
      },
      ...[...list, ...missing].map(clientOption),
    ];
  }, [clients.data, current, t]);
  return { available: canView && !clients.isError, loading: clients.isPending, options };
}

/** Active staff for choosing a person, the signed-in person marked "You". */
export function useStaffOptions(enabled = true) {
  const t = useT();
  const me = useCurrentUser();
  const staff = useActiveStaff(enabled);
  const options = useMemo<ComboboxOption[]>(
    () =>
      (staff.data ?? []).map((person) =>
        personOption(person, person.id === me.id ? t("work.you") : t(`role.${person.role}`)),
      ),
    [staff.data, me.id, t],
  );
  return { options, loading: staff.isPending, available: !staff.isError };
}
